import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { UsersApiService } from '../../services/users-api.service';
import { ProductsApiService } from '../../services/products-api.service';
import { RolesApiService } from '../../services/roles-api.service';
import { CamelToTitlePipe } from '../../shared/camel-to-title.pipe';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { BASE_VIO_TABLE_V2_CONFIG } from '../../shared/vio-table-v2.config';
import { openModalById } from '../../shared/bootstrap-modal.helper';

interface Assignment {
  productId: string;
  stepIds: string[];
}

interface UserRow {
  _id: string;
  name: string;
  username: string;
  role: string;
  language: string;
  active: boolean;
  assignments: Assignment[];
}

interface Product {
  _id: string;
  name: string;
  steps: { _id: string; name: string }[];
}

interface RoleOption {
  _id: string;
  role_name: string;
}

@Component({
  selector: 'app-operators',
  standalone: true,
  imports: [CommonModule, FormsModule, CamelToTitlePipe, AppendToBodyDirective, VioTablesV2Module],
  templateUrl: './operators.component.html',
  styleUrl: './operators.component.scss',
})
export class OperatorsComponent implements OnInit {
  users: UserRow[] = [];
  tableData: any[] = [];
  products: Product[] = [];
  roles: RoleOption[] = [];
  loading = true;

  private camelToTitle = new CamelToTitlePipe();

  tableConfig: VT2_TABLE_CONFIG = {
    ...BASE_VIO_TABLE_V2_CONFIG,
    tableTitle: 'Users',
    excelFileName: 'users',
    excludeFields: ['_id'],
    columnLabels: { name: 'Name', username: 'Username', role: 'Role', language: 'Language' },
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit user',
        cssClass: 'btn btn-outline-primary btn-sm vio-table-icon-btn vio-icon-edit',
        onClick: (row: any) => {
          const user = this.users.find((u) => u._id === row._id);
          if (!user) return;
          this.openEditModal(user);
          openModalById('addOperatorModal');
        },
      },
      {
        icon: '🌐',
        label: 'Language',
        title: 'Language',
        cssClass: 'btn btn-outline-primary btn-sm vio-table-icon-btn vio-icon-language',
        onClick: (row: any) => openModalById('languageModal-' + row._id),
      },
      {
        icon: '📋',
        label: 'Assignments',
        title: 'Assignments',
        cssClass: 'btn btn-outline-info btn-sm vio-table-icon-btn vio-icon-assignments',
        onClick: (row: any) => openModalById('assignmentsModal-' + row._id),
      },
    ],
  };

  // The Add/Edit User modal is a single static instance (like Roles/Machines), reused for both —
  // operation + editingUserId decide what it's currently doing. Password is required to add a
  // user but optional to edit one (blank means "leave unchanged"), so it's kept out of the
  // update payload entirely when left blank in Edit mode.
  operation: 'Add' | 'Edit' = 'Add';
  editingUserId: string | null = null;
  newUser = { name: '', username: '', password: '', role: 'operator', language: 'en' };
  showNewUserPassword = false;

  @ViewChild('addOperatorCloseBtn') addOperatorCloseBtn!: ElementRef<HTMLButtonElement>;

  private toastr = inject(ToastrService);

  constructor(
    private usersApi: UsersApiService,
    private productsApi: ProductsApiService,
    private rolesApi: RolesApiService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.load();
  }

  async load() {
    this.loading = true;
    const [usersRes, productsRes, rolesRes] = await Promise.all([this.usersApi.list(), this.productsApi.list(), this.rolesApi.list()]);
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `loading` flips to false internally but the view stays stuck.
    this.ngZone.run(() => {
      if (usersRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.users = usersRes.Data;
      } else {
        reportApiError(this.toastr, usersRes);
      }
      if (productsRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.products = productsRes.Data;
      } else {
        reportApiError(this.toastr, productsRes);
      }
      if (rolesRes.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.roles = rolesRes.Data;
      } else {
        reportApiError(this.toastr, rolesRes);
      }
      this.tableData = this.users.map((u) => ({
        _id: u._id,
        name: u.name,
        username: u.username,
        role: this.camelToTitle.transform(u.role),
        language: u.language === 'mr' ? 'Marathi' : 'English',
      }));
      this.loading = false;
    });
  }

  openAddModal() {
    this.operation = 'Add';
    this.editingUserId = null;
    this.newUser = { name: '', username: '', password: '', role: 'operator', language: 'en' };
    this.showNewUserPassword = false;
  }

  openEditModal(user: UserRow) {
    this.operation = 'Edit';
    this.editingUserId = user._id;
    this.newUser = { name: user.name, username: user.username, password: '', role: user.role, language: user.language };
    this.showNewUserPassword = false;
  }

  async saveUser() {
    if (!this.newUser.name || !this.newUser.username) return;
    if (this.operation === 'Add') {
      if (!this.newUser.password) return;
      const res = await this.usersApi.create(this.newUser);
      if (!notifyResult(this.toastr, res, `${this.newUser.name} added.`)) return;
    } else {
      const { password, ...rest } = this.newUser;
      const payload: Record<string, any> = { ...rest };
      if (password) payload['password'] = password;
      const res = await this.usersApi.update(this.editingUserId!, payload);
      if (!notifyResult(this.toastr, res, `${this.newUser.name} updated.`)) return;
    }
    this.addOperatorCloseBtn.nativeElement.click();
    await this.load();
  }

  async saveLanguage(user: UserRow) {
    const res = await this.usersApi.update(user._id, { language: user.language });
    if (!notifyResult(this.toastr, res, 'Language preference saved.')) return;
    this.closeModal('languageModal-' + user._id);
    await this.load();
  }

  isAssigned(user: UserRow, productId: string, stepId: string): boolean {
    const assignment = user.assignments.find((a) => a.productId === productId);
    return !!assignment && assignment.stepIds.includes(stepId);
  }

  toggleStepAssignment(user: UserRow, productId: string, stepId: string) {
    let assignment = user.assignments.find((a) => a.productId === productId);
    if (!assignment) {
      assignment = { productId, stepIds: [] };
      user.assignments.push(assignment);
    }
    const idx = assignment.stepIds.indexOf(stepId);
    if (idx === -1) assignment.stepIds.push(stepId);
    else assignment.stepIds.splice(idx, 1);
  }

  // A product's steps only render (see the modal template) while its section is expanded — with
  // some products carrying 60+ steps, rendering every product's full step grid at once made the
  // modal an unscannable wall of checkboxes. Collapsed-by-default accordion sections keep it to
  // one product's worth of steps on screen at a time.
  private expandedProductIds = new Set<string>();

  isProductExpanded(productId: string): boolean {
    return this.expandedProductIds.has(productId);
  }

  toggleProductExpanded(productId: string) {
    if (this.expandedProductIds.has(productId)) this.expandedProductIds.delete(productId);
    else this.expandedProductIds.add(productId);
  }

  assignedCount(user: UserRow, product: Product): number {
    const assignment = user.assignments.find((a) => a.productId === product._id);
    return assignment ? assignment.stepIds.filter((id) => product.steps.some((s) => s._id === id)).length : 0;
  }

  selectAllSteps(user: UserRow, product: Product) {
    const assignment = user.assignments.find((a) => a.productId === product._id);
    const stepIds = product.steps.map((s) => s._id);
    if (assignment) assignment.stepIds = stepIds;
    else user.assignments.push({ productId: product._id, stepIds });
  }

  selectNoSteps(user: UserRow, product: Product) {
    const assignment = user.assignments.find((a) => a.productId === product._id);
    if (assignment) assignment.stepIds = [];
  }

  async saveAssignments(user: UserRow) {
    const assignments = user.assignments.filter((a) => a.stepIds.length > 0);
    const res = await this.usersApi.update(user._id, { assignments });
    if (!notifyResult(this.toastr, res, 'Assignments saved.')) return;
    this.closeModal('assignmentsModal-' + user._id);
    await this.load();
  }

  // Per-row modals (Language / Assignments) are id'd per user, so there's no single ViewChild
  // close button to click like the static Add-modal pattern — hide the specific instance directly.
  private closeModal(id: string) {
    const element = document.getElementById(id);
    if (element) (window as any).bootstrap.Modal.getInstance(element)?.hide();
  }

  // See ProductsComponent.trackByProductId — without this, reloading while a per-user modal
  // (Language / Assignments) is open tears that modal's DOM node out mid-use and orphans its
  // Bootstrap backdrop, freezing the page.
  trackByUserId(_index: number, user: UserRow): string {
    return user._id;
  }
}
